import { useEffect, useMemo, useRef, useState } from 'react';
import { toFlag } from 'pano-utils';
import {
  type ComponentLayer,
  type ComponentLayerIconPosition,
  type ComponentLayerSize,
  ImageType,
} from '@types';
import { Validator } from '@components/Validator';
import { $editingComponentLayerApi } from '@stores/editingComponentLayerStores';
import { Collapse } from '@components/Collapse';
import ImageContainer from '@components/ImageContainer';
import Button from '@components/Button';
import Tip from '@components/Tip';
import Image from '@components/Image';
import { selectLibCLayerIcons } from '@modules/mediaLib';
import Input from '@components/Input/Input';
import useInputHandlers from '@hooks/useInputHandelrs';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import { $viewMode, $viewModeApi } from '@stores/viewModeStores';
import Tabbar from '@components/Tabbar';
import getImageSize from '@utils/getImageSize';
import { getMediaUrl, getSysUrl, getUrlByMediaType } from '@utils/urlMakers';
import FeatureToggle from '@components/FeatureToggle';
import cn from '@utils/cn';
import ColorPicker from '@components/ColorPicker';
import { C_LAYER_DEFAULT } from '@modules/componentLayer';
import { roundNum } from '@utils/roundNum';
import useToggle from '@hooks/useToggle';
import { NumInput } from '@components/NumInput';

interface Props {
  data: ComponentLayer;
}

/** 标题最大长度 */
const TITLE_LENGTH = 50;

/** 终端显示类型 */
type DisplayType = 'both' | 'desktop' | 'mobile';

/** 通过获取终端显示 */
const getDisplayType = (devices: ComponentLayer['devices']): DisplayType => {
  if (devices.length === 1) {
    return devices[0] === 0 ? 'mobile' : 'desktop';
  }
  return 'both';
};

/** 通过显示类型值获取 devices */
const getDevicesByDisplayType = (
  displayType: DisplayType
): ComponentLayer['devices'] => {
  if (displayType === 'both') return [0, 1];
  return [displayType === 'mobile' ? 0 : 1];
};

/** 终端下拉菜单项 */
const deviceItems: DropdownItem<DisplayType>[] = [
  { id: 'both', name: '同时显示' },
  { id: 'desktop', name: '仅桌面端显示' },
  { id: 'mobile', name: '仅移动端显示' },
];

/** 显示状态下拉菜单项 */
const visibleItems: DropdownItem<ComponentLayer['visible']>[] = [
  { id: true, name: '默认显示' },
  { id: false, name: '默认隐藏' },
];

/** 显示状态下拉菜单项 */
const displayModeItems: DropdownItem<ComponentLayer['displayMode']>[] = [
  { id: 0, name: '普通模式' },
  { id: 1, name: '灯箱模式' },
];

/** 图层尺寸类型菜单项 */
const sizeTypeItems: DropdownItem<ComponentLayerSize['type']>[] = [
  { id: 0, name: '固定大小' },
  { id: 1, name: '自适应' },
];

/** 宽高占比类型菜单项 */
const adaptiveItems: DropdownItem<ComponentLayerSize['adaptiveBy']>[] = [
  { id: 0, name: '图层宽占比' },
  { id: 1, name: '图层高占比' },
];

/** 位置选项列表 */
const positionItems: {
  key: ComponentLayerIconPosition['edge'];
  name: string;
}[] = [
  { key: 'leftTop', name: '左上' },
  { key: 'topCenter', name: '顶部居中' },
  { key: 'rightTop', name: '右上' },
  { key: 'leftCenter', name: '左侧居中' },
  { key: 'center', name: '正中' },
  { key: 'rightCenter', name: '右侧居中' },
  { key: 'leftBottom', name: '左下' },
  { key: 'bottomCenter', name: '底部居中' },
  { key: 'rightBottom', name: '右下' },
];

export function LayerStyleSettings({ data }: Props) {
  const {
    icon,
    title,
    devices,
    size,
    visible,
    displayMode,
    bgColor,
    turnOffBgMusicOnShow,
    turnOffRotateOnShow,
    hideInClearMode,
  } = data;
  // 当前显示类型, 同时显示、桌面端、移动端
  const displayType: DisplayType = useMemo(
    () => getDisplayType(devices),
    [devices]
  );
  const [styleTab, setStyleTab] = useState(() => {
    if (displayType === 'both')
      return $viewMode.getState() === 'mobile' ? 1 : 0;
    return displayType === 'mobile' ? 1 : 0;
  });
  // 用于存储所选图像的宽度、高度、宽高比
  const imageSizeRef = useRef({ width: 0, height: 0, ratio: 1 });
  const imageLoading = useToggle(false);

  useEffect(() => {
    // 组件加载时，获取图片的宽度、高度、宽高比
    if (icon.mediaUrl) {
      getImageSize(getUrlByMediaType(icon.mediaType, icon.mediaUrl)).then(
        (imageSize) => {
          if (imageSize) {
            imageSizeRef.current = {
              ...imageSize,
              ratio: imageSize.width / imageSize.height,
            };
          }
        }
      );
    }

    // 组件加载时如果显示类型为单一类型, 则切换
    if (displayType === 'mobile') {
      $viewModeApi.mobile();
    } else if (displayType === 'desktop') {
      $viewModeApi.pc();
    }
  }, []);

  /**
   * 选择图片
   */
  const handleImageSelect = async () => {
    imageLoading.on();
    const { libItems, sysItems } = await selectLibCLayerIcons();
    if (libItems) {
      const item = libItems[0];
      const srcSize = await getImageSize(getMediaUrl(item.url));
      if (srcSize) {
        // 保存图片的宽度、高度、宽高比
        imageSizeRef.current = {
          ...srcSize,
          ratio: srcSize.width / srcSize.height,
        };
        $editingComponentLayerApi.update((draft) => {
          draft.icon.mediaType = 1;
          draft.icon.mediaId = item.id;
          draft.icon.mediaUrl = item.url;
          // 图片选择后，标题默认为图片名称, 不能超过50个字符
          draft.title = item.name.slice(0, TITLE_LENGTH);
          // 固定大小模式设置图层宽高
          if (draft.size.desktop.type === 0) {
            draft.size.desktop.width = srcSize.width;
            draft.size.desktop.height = srcSize.height;
          }
          if (draft.size.mobile.type === 0) {
            draft.size.mobile.width = srcSize.width;
            draft.size.mobile.height = srcSize.height;
          }
        });
      }
    } else if (sysItems) {
      const item = sysItems[0];
      const srcSize = await getImageSize(getSysUrl(item.url));
      if (srcSize) {
        // 保存图片的宽度、高度、宽高比
        imageSizeRef.current = {
          ...srcSize,
          ratio: srcSize.width / srcSize.height,
        };
        $editingComponentLayerApi.update((draft) => {
          draft.icon.mediaType = 0;
          draft.icon.mediaId = 0;
          draft.icon.mediaUrl = item.url;
          // 固定大小模式设置图层宽高
          if (draft.size.desktop.type === 0) {
            draft.size.desktop.width = srcSize.width;
            draft.size.desktop.height = srcSize.height;
          }
          if (draft.size.mobile.type === 0) {
            draft.size.mobile.width = srcSize.width;
            draft.size.mobile.height = srcSize.height;
          }
        });
      }
    }
    imageLoading.off();
  };

  /**
   * 标题输入框事件
   */
  const titleHandlers = useInputHandlers((val) => {
    $editingComponentLayerApi.update((draft) => {
      draft.title = val;
    });
  });

  /**
   * 终端显示类型修改
   */
  const handleDisplayTypeChange = (type: DisplayType) => {
    $editingComponentLayerApi.update((draft) => {
      draft.devices = getDevicesByDisplayType(type);
    });
    // 同时切换播放器显示状态
    if (type === 'mobile') {
      $viewModeApi.mobile();
    } else {
      $viewModeApi.pc();
    }
    // 如果是同时显示，样式设置选中桌面端样式
    if (type === 'both') {
      setStyleTab(0);
    }
  };

  /**
   * 样式设置tab切换
   */
  const handleStyleTabChange = (index: number) => {
    // 样式tab切换时同时切换播放器显示状态
    if (index === 1) {
      $viewModeApi.mobile();
    } else {
      $viewModeApi.pc();
    }
    setStyleTab(index);
  };

  const renderSettings = (type: 'mobile' | 'desktop') => {
    const sizeData = size[type];
    const posData = icon[type];

    /** 宽高数值修改 */
    const handlePxInputChange =
      (key: 'width' | 'height') => (value: number) => {
        $editingComponentLayerApi.update((draft) => {
          draft.size[type][key] = value;
          // 如果锁定宽高比，另一个值也要跟着变
          if (draft.size[type].lockAspectRatio) {
            if (key === 'width') {
              draft.size[type].height = Math.round(
                value / imageSizeRef.current.ratio
              );
            } else {
              draft.size[type].width = Math.round(
                value * imageSizeRef.current.ratio
              );
            }
          }
        });
      };

    /** 宽高百分比修改 */
    const handlePercentInputChange =
      (key: 'width' | 'height') => (value: number) => {
        $editingComponentLayerApi.update((draft) => {
          const val = value / 100;
          // 如果锁定宽高比，另一个值也要跟着变
          if (draft.size[type].lockAspectRatio) {
            draft.size[type].width = val;
            draft.size[type].height = val;
          } else {
            draft.size[type][key] = val;
          }
        });
      };

    /** 偏移值修改 */
    const handleOffsetInputChange =
      (key: 'offsetX' | 'offsetY') => (value: number) => {
        $editingComponentLayerApi.update((draft) => {
          draft.icon[type][key] = Number(value);
        });
      };

    return (
      <>
        <div className="mt-20 mb-10">图层尺寸</div>
        <Dropdown
          items={sizeTypeItems}
          selected={sizeData.type}
          onChange={(val) => {
            if (val === 0) {
              // 切换到固定大小
              $editingComponentLayerApi.update((draft) => {
                draft.size[type].type = 0;
                draft.size[type].width = imageSizeRef.current.width;
                draft.size[type].height = imageSizeRef.current.height;
              });
            } else {
              // 切换到自适应
              $editingComponentLayerApi.update((draft) => {
                draft.size[type].type = 1;
                draft.size[type].adaptiveBy = 0;
                draft.size[type].width = 0.5;
                draft.size[type].height = 0.5;
              });
            }
          }}
        />
        <FeatureToggle
          title="锁定宽高比"
          isOn={!!sizeData.lockAspectRatio}
          onToggle={(isOn) => {
            // 自适应模式下, 只需要改开关值
            if (sizeData.type === 1) {
              $editingComponentLayerApi.update((draft) => {
                draft.size[type].lockAspectRatio = toFlag(isOn);
              });
              return;
            }
            // 固定大小模式
            if (isOn) {
              // 锁定宽高比
              $editingComponentLayerApi.update((draft) => {
                draft.size[type].lockAspectRatio = 1;
                // 开启后，宽度不变，高度自适应调整
                draft.size[type].height = Math.round(
                  draft.size[type].width / imageSizeRef.current.ratio
                );
              });
            } else {
              // 不锁定宽高比
              $editingComponentLayerApi.update((draft) => {
                draft.size[type].lockAspectRatio = 0;
              });
            }
          }}
        />
        {sizeData.type === 0 && (
          <>
            <div className="mt-20 flex items-center">
              <div>宽度</div>
              <NumInput
                className="ml-auto w-80 px-10"
                min={0}
                max={9999}
                value={sizeData.width}
                onChange={handlePxInputChange('width')}
              />
              <div className="ml-5 text-black-gray">PX</div>
            </div>
            <div className="mt-20 flex items-center">
              <div>高度</div>
              <NumInput
                className="ml-auto w-80 px-10"
                min={0}
                max={9999}
                value={sizeData.height}
                onChange={handlePxInputChange('height')}
              />
              <div className="ml-5 text-black-gray">PX</div>
            </div>
          </>
        )}
        {sizeData.type === 1 && (
          <>
            {sizeData.lockAspectRatio === 1 && (
              <div className="mt-20 flex items-center">
                <Dropdown
                  className="w-125"
                  items={adaptiveItems}
                  selected={sizeData.adaptiveBy}
                  onChange={(val) => {
                    $editingComponentLayerApi.update((draft) => {
                      draft.size[type].adaptiveBy = val;
                    });
                  }}
                />
                {sizeData.adaptiveBy === 0 && (
                  <NumInput
                    className="ml-auto w-60"
                    value={roundNum(0, sizeData.width * 100)}
                    min={0}
                    max={100}
                    onChange={handlePercentInputChange('width')}
                  />
                )}
                {sizeData.adaptiveBy === 1 && (
                  <NumInput
                    className="ml-auto w-60"
                    value={roundNum(0, sizeData.height * 100)}
                    min={0}
                    max={100}
                    onChange={handlePercentInputChange('height')}
                  />
                )}
                <div className="ml-5 text-black-gray">%</div>
              </div>
            )}
            {sizeData.lockAspectRatio === 0 && (
              <>
                <div className="mt-20 flex items-center">
                  <div>图层宽占比</div>
                  <NumInput
                    className="ml-auto w-60"
                    value={roundNum(0, sizeData.width * 100)}
                    min={0}
                    max={100}
                    onChange={handlePercentInputChange('width')}
                  />
                  <div className="ml-5 text-black-gray">%</div>
                </div>
                <div className="mt-20 flex items-center">
                  <div>图层高占比</div>
                  <NumInput
                    className="ml-auto w-60"
                    value={roundNum(0, sizeData.height * 100)}
                    min={0}
                    max={100}
                    onChange={handlePercentInputChange('height')}
                  />
                  <div className="ml-5 text-black-gray">%</div>
                </div>
              </>
            )}
            <Tip className="mt-10">另外一边的尺寸等比例自动缩放显示</Tip>
          </>
        )}
        <div className="mt-20">图层位置</div>
        <div className="flex flex-wrap items-center justify-between">
          {positionItems.map(({ key, name }) => (
            <div
              key={key}
              className={cn(
                'mt-10 flex h-70 w-70 cursor-pointer items-center justify-center border border-black-#34',
                {
                  'bg-blue text-white': posData.edge === key,
                  'bg-black text-black-gray': posData.edge !== key,
                }
              )}
              onClick={() => {
                $editingComponentLayerApi.update((draft) => {
                  draft.icon[type].edge = key;
                  // 切换位置时同时清除偏移值
                  draft.icon[type].offsetX = 0;
                  draft.icon[type].offsetY = 0;
                });
              }}
            >
              {name}
            </div>
          ))}
        </div>
        <div className="mt-20 flex items-center">
          <div>X轴相对偏移值</div>
          <NumInput
            className="ml-auto w-80 px-10"
            min={-9999}
            max={9999}
            value={posData.offsetX}
            onChange={handleOffsetInputChange('offsetX')}
          />
          <div className="ml-5 text-black-gray">PX</div>
        </div>
        <div className="mt-20 flex items-center">
          <div>Y轴相对偏移值</div>
          <NumInput
            className="ml-auto w-80 px-10"
            min={-9999}
            max={9999}
            value={posData.offsetY}
            onChange={handleOffsetInputChange('offsetY')}
          />
          <div className="ml-5 text-black-gray">PX</div>
        </div>
        <Tip className="mt-20">注：输入负数时为反方向偏移</Tip>
      </>
    );
  };

  return (
    <>
      <Collapse title="选择图层">
        <Validator
          value={icon.mediaUrl}
          validFn={(val) => !!val}
          warningMessage="请选择图片"
        >
          {({ value, warning }) => (
            <ImageContainer
              className="h-115"
              placeholder={
                <div className="flex flex-col items-center">
                  <p>图片长/宽不超过2000PX</p>
                  <p>支持JPG/PNG/GIF</p>
                </div>
              }
              warning={warning}
              onDeleteClick={() => {
                $editingComponentLayerApi.update((draft) => {
                  return {
                    ...C_LAYER_DEFAULT,
                    id: draft.id,
                    zIndex: draft.zIndex,
                  };
                });
              }}
            >
              {!!value && (
                <Image
                  src={value}
                  type={icon.mediaType ? ImageType.media : ImageType.sysMedia}
                  className="max-h-full max-w-full"
                />
              )}
            </ImageContainer>
          )}
        </Validator>
        <div className="mt-10 flex items-center justify-between">
          <Tip>建议大小：不超过700KB</Tip>
          <Button
            size="sm"
            onClick={handleImageSelect}
            disabled={imageLoading.isOn}
          >
            选择图片
          </Button>
        </div>
      </Collapse>
      {!!icon.mediaUrl && (
        <>
          <Collapse title="图层标题">
            <Validator
              value={title}
              validFn={(val) => !!val.trim()}
              warningMessage="请输入图层标题"
            >
              {({ value, warning }) => (
                <Input
                  maxLength={TITLE_LENGTH}
                  scheme="dark"
                  className="h-30"
                  placeholder="图层标题"
                  warning={warning}
                  value={value}
                  {...titleHandlers}
                />
              )}
            </Validator>
          </Collapse>

          <Collapse title="显示设置">
            <div className="mt-20 mb-10">终端</div>
            <Dropdown
              items={deviceItems}
              selected={displayType}
              onChange={handleDisplayTypeChange}
            />
            <div className="mt-20 mb-10">状态</div>
            <Dropdown
              items={visibleItems}
              selected={visible}
              onChange={(val) => {
                $editingComponentLayerApi.update((draft) => {
                  draft.visible = val;
                });
              }}
            />
            <div className="mt-20 mb-10">模式</div>
            <Dropdown
              items={displayModeItems}
              selected={displayMode}
              onChange={(val) => {
                $editingComponentLayerApi.update((draft) => {
                  draft.displayMode = val;
                  draft.bgColor = C_LAYER_DEFAULT.bgColor;
                  draft.turnOffBgMusicOnShow =
                    C_LAYER_DEFAULT.turnOffBgMusicOnShow;
                  draft.turnOffRotateOnShow =
                    C_LAYER_DEFAULT.turnOffRotateOnShow;
                });
              }}
            />
            {displayMode === 1 && (
              <>
                <ColorPicker
                  className="mt-20"
                  colorString={bgColor}
                  onChange={(str) =>
                    $editingComponentLayerApi.update((draft) => {
                      draft.bgColor = str;
                    })
                  }
                >
                  <ColorPicker.Header>
                    <div>灯箱蒙层背景颜色</div>
                    <ColorPicker.Reset defaultColor={C_LAYER_DEFAULT.bgColor} />
                  </ColorPicker.Header>
                  <ColorPicker.Bar presetColors={[]} />
                </ColorPicker>
                <FeatureToggle
                  title="显示时暂停背景音乐"
                  isOn={!!turnOffBgMusicOnShow}
                  onToggle={(val) => {
                    $editingComponentLayerApi.update((draft) => {
                      draft.turnOffBgMusicOnShow = toFlag(val);
                    });
                  }}
                />
                <FeatureToggle
                  title="显示时暂停导览讲解"
                  isOn={!!turnOffRotateOnShow}
                  onToggle={(val) => {
                    $editingComponentLayerApi.update((draft) => {
                      draft.turnOffRotateOnShow = toFlag(val);
                    });
                  }}
                />
              </>
            )}
            {displayMode === 0 && (
              <FeatureToggle
                title="清屏时同步隐藏该图层"
                isOn={!!hideInClearMode}
                onToggle={(val) => {
                  $editingComponentLayerApi.update((draft) => {
                    draft.hideInClearMode = toFlag(val);
                  });
                }}
              />
            )}
          </Collapse>
          <Collapse title="样式设置">
            {displayType === 'both' && (
              <Tabbar
                className="mb-10"
                tabs={['桌面端样式', '移动端样式']}
                activeIndex={styleTab}
                onChange={handleStyleTabChange}
              />
            )}
            {(displayType === 'desktop' ||
              (displayType === 'both' && styleTab === 0)) &&
              renderSettings('desktop')}
            {(displayType === 'mobile' ||
              (displayType === 'both' && styleTab === 1)) &&
              renderSettings('mobile')}
          </Collapse>
        </>
      )}
    </>
  );
}

export default LayerStyleSettings;
